import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bell, AlertTriangle, Clock, FileText, Award, Users, ChevronRight, RefreshCw, Info, Flame } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../../lib/api';

const REMINDER_CONFIG = {
    inactive_mandate: { icon: Clock, color: 'text-yellow-400', bg: 'bg-yellow-500/10', border: 'border-yellow-500/20' },
    no_offers: { icon: AlertTriangle, color: 'text-orange-400', bg: 'bg-orange-500/10', border: 'border-orange-500/20' },
    pending_rating: { icon: FileText, color: 'text-blue-400', bg: 'bg-blue-500/10', border: 'border-blue-500/20' },
    certification: { icon: Award, color: 'text-[#FF914D]', bg: 'bg-[#FF914D]/10', border: 'border-[#FF914D]/20' },
    inter_agent_approval: { icon: Users, color: 'text-[#681793]', bg: 'bg-[#681793]/10', border: 'border-[#681793]/20' }
};

// ═══════════════════════════════════════════════════════════════════════
// Priority taxonomy : urgent (high) / important (medium) / informatif (low)
// ═══════════════════════════════════════════════════════════════════════
const CATEGORIES = [
    {
        key: 'urgent',
        label: 'Urgent',
        sub: 'À traiter aujourd\'hui',
        priority: 'high',
        icon: Flame,
        color: 'text-red-400',
        bg: 'bg-red-500/10',
        border: 'border-red-500/30',
        activeBg: 'bg-red-500/15',
        activeBorder: 'border-red-500/50',
    },
    {
        key: 'important',
        label: 'Important',
        sub: 'Cette semaine',
        priority: 'medium',
        icon: AlertTriangle,
        color: 'text-amber-400',
        bg: 'bg-amber-500/10',
        border: 'border-amber-500/30',
        activeBg: 'bg-amber-500/15',
        activeBorder: 'border-amber-500/50',
    },
    {
        key: 'info',
        label: 'Informatif',
        sub: 'Quand vous avez un moment',
        priority: 'low',
        icon: Info,
        color: 'text-blue-400',
        bg: 'bg-blue-500/10',
        border: 'border-blue-500/30',
        activeBg: 'bg-blue-500/15',
        activeBorder: 'border-blue-500/50',
    },
];

export default function SmartReminders({ basePath = '/agent' }) {
    const [reminders, setReminders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [collapsed, setCollapsed] = useState(false);
    const [activeTab, setActiveTab] = useState('urgent');
    const navigate = useNavigate();

    useEffect(() => { load(); }, []);

    const load = async () => {
        setLoading(true);
        try {
            const res = await api.get('/smart-reminders/');
            setReminders(res.data.reminders || []);
        } catch { /* silent */ }
        finally { setLoading(false); }
    };

    const handleClick = (reminder) => {
        if (reminder.mandate_id) {
            navigate(`${basePath}/mandates/v2/${reminder.mandate_id}`);
        } else if (reminder.type === 'certification') {
            navigate(`${basePath}/certifications`);
        } else if (reminder.type === 'inter_agent_approval') {
            navigate(`${basePath}/offers`);
        }
    };

    // Group reminders by category
    const { grouped, totals } = useMemo(() => {
        const g = { urgent: [], important: [], info: [] };
        const t = { urgent: 0, important: 0, info: 0 };
        reminders.forEach(r => {
            const cat = CATEGORIES.find(c => c.priority === r.priority);
            const key = cat ? cat.key : 'info';
            g[key].push(r);
            t[key] += 1;
        });
        return { grouped: g, totals: t };
    }, [reminders]);

    // Auto-select tab with content (prefer urgent)
    useEffect(() => {
        if (totals.urgent > 0) setActiveTab('urgent');
        else if (totals.important > 0) setActiveTab('important');
        else if (totals.info > 0) setActiveTab('info');
    }, [totals.urgent, totals.important, totals.info]);

    if (loading) return <div className="animate-pulse bg-card rounded-2xl h-24 border border-border" />;
    if (reminders.length === 0) return null;

    const activeCategory = CATEGORIES.find(c => c.key === activeTab) || CATEGORIES[0];
    const activeReminders = grouped[activeTab] || [];
    const totalCount = reminders.length;

    return (
        <div className="bg-card rounded-2xl border border-border overflow-hidden" data-testid="smart-reminders">
            {/* Header */}
            <button
                onClick={() => setCollapsed(!collapsed)}
                className="w-full p-4 flex items-center justify-between hover:bg-white/[0.02] transition-colors"
                data-testid="cockpit-alertes-toggle"
            >
                <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#FF914D] to-[#681793] flex items-center justify-center relative">
                        <Bell className="w-5 h-5 text-white" />
                        {totals.urgent > 0 && (
                            <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center animate-pulse">
                                {totals.urgent}
                            </span>
                        )}
                    </div>
                    <div className="text-left">
                        <h3 className="text-sm font-bold text-foreground">Cockpit alertes</h3>
                        <p className="text-[10px] text-muted-foreground">
                            {totalCount} alerte{totalCount > 1 ? 's' : ''} · {totals.urgent} urgente{totals.urgent > 1 ? 's' : ''}
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <span role="button" tabIndex={0} onClick={(e) => { e.stopPropagation(); load(); }} className="inline-flex items-center justify-center h-7 w-7 rounded-md text-muted-foreground hover:bg-white/10 transition-colors cursor-pointer" data-testid="cockpit-alertes-refresh">
                        <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
                    </span>
                    <ChevronRight className={`w-4 h-4 text-muted-foreground transition-transform ${collapsed ? '' : 'rotate-90'}`} />
                </div>
            </button>

            <AnimatePresence>
                {!collapsed && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="overflow-hidden"
                    >
                        {/* Category tabs */}
                        <div className="px-3 pt-1 pb-2 flex gap-1.5 border-b border-border">
                            {CATEGORIES.map(cat => {
                                const count = totals[cat.key];
                                const isActive = activeTab === cat.key;
                                const disabled = count === 0;
                                return (
                                    <button
                                        key={cat.key}
                                        onClick={() => !disabled && setActiveTab(cat.key)}
                                        disabled={disabled}
                                        className={`flex-1 px-2 py-2 rounded-lg border transition-all flex items-center justify-center gap-1.5 ${
                                            isActive
                                                ? `${cat.activeBg} ${cat.activeBorder} ${cat.color}`
                                                : disabled
                                                    ? 'bg-transparent border-transparent text-muted-foreground/40 cursor-not-allowed'
                                                    : 'bg-white/[0.02] border-white/5 text-muted-foreground hover:text-foreground hover:bg-white/[0.05]'
                                        }`}
                                        data-testid={`cockpit-alertes-tab-${cat.key}`}
                                    >
                                        <cat.icon className={`w-3.5 h-3.5 ${isActive ? cat.color : ''}`} />
                                        <span className="text-[11px] font-bold">{cat.label}</span>
                                        {count > 0 && (
                                            <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-black ${isActive ? `${cat.bg}` : 'bg-white/5'}`}>
                                                {count}
                                            </span>
                                        )}
                                    </button>
                                );
                            })}
                        </div>

                        {/* Category sub-header */}
                        <div className="px-4 pt-3 pb-2 flex items-center gap-2">
                            <activeCategory.icon className={`w-3 h-3 ${activeCategory.color}`} />
                            <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-bold">{activeCategory.sub}</p>
                        </div>

                        {/* Active category reminders */}
                        <div className="px-4 pb-4 space-y-2">
                            <AnimatePresence mode="wait">
                                {activeReminders.length === 0 ? (
                                    <motion.div
                                        key={`empty-${activeTab}`}
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        exit={{ opacity: 0 }}
                                        className="py-6 text-center"
                                    >
                                        <activeCategory.icon className={`w-6 h-6 ${activeCategory.color} opacity-40 mx-auto mb-2`} />
                                        <p className="text-xs text-muted-foreground">Aucune alerte dans cette catégorie</p>
                                    </motion.div>
                                ) : (
                                    activeReminders.map((reminder, idx) => {
                                        const config = REMINDER_CONFIG[reminder.type] || REMINDER_CONFIG.inactive_mandate;
                                        const Icon = config.icon;
                                        return (
                                            <motion.button
                                                key={`${activeTab}-${idx}`}
                                                initial={{ opacity: 0, x: -10 }}
                                                animate={{ opacity: 1, x: 0 }}
                                                exit={{ opacity: 0 }}
                                                transition={{ delay: idx * 0.04 }}
                                                onClick={() => handleClick(reminder)}
                                                className={`w-full text-left p-3 rounded-xl ${config.bg} ${config.border} border hover:brightness-110 transition-all group`}
                                                data-testid={`reminder-${activeTab}-${idx}`}
                                            >
                                                <div className="flex items-start gap-3">
                                                    <Icon className={`w-4 h-4 ${config.color} shrink-0 mt-0.5`} />
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-xs font-bold text-foreground">{reminder.title}</p>
                                                        <p className="text-[10px] text-muted-foreground truncate">{reminder.message}</p>
                                                        <p className="text-[10px] text-[#FF914D] mt-1 font-medium">{reminder.action}</p>
                                                    </div>
                                                    <ChevronRight className="w-3 h-3 text-white/20 group-hover:text-[#FF914D] shrink-0 mt-1 transition-colors" />
                                                </div>
                                            </motion.button>
                                        );
                                    })
                                )}
                            </AnimatePresence>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}
